import React, { useState, useEffect } from 'react'
import { Card, Image } from 'react-bootstrap'

import { Link } from 'react-router-dom';

import aiProfileImage from '../../../../assets/images/user/ai-chat.png';
import learningHelperProfileImage from '../../../../assets/images/user/learning-helper.png';
import { useSelector } from 'react-redux';

const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours);
    const min = parseInt(minutes);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${min.toString().padStart(2, '0')} ${ampm}`;
};

const cleanNamePart = (value) => {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    const normalized = trimmed.toLowerCase();
    if (normalized === 'undefined' || normalized === 'null') return '';
    return trimmed;
};

const getDisplayName = (user) => {
    const firstName = cleanNamePart(user?.first_name);
    const lastName = cleanNamePart(user?.last_name);
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || cleanNamePart(user?.slug) || 'User';
};

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_KEYS  = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

const OrgHoursCard = ({ organizationInfo }) => {
    const [open, setOpen] = useState(false);

    const hours = DAY_LABELS
        .map((day, i) => ({
            day,
            short: DAY_SHORT[i],
            start: organizationInfo[`${DAY_KEYS[i]}_start_time`],
            end:   organizationInfo[`${DAY_KEYS[i]}_end_time`],
        }))
        .filter(({ start }) => start);

    const todayHours = hours.find(h => h.day === today);

    return (
        <div style={{
            borderRadius: '10px',
            marginBottom: '14px',
            overflow: 'hidden',
            border: '1px solid #ede9f8',
            background: '#fff',
            boxShadow: '0 1px 6px rgba(92,81,139,0.08)',
        }}>
            {/* Compact header row — always visible */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 10px',
                background: 'linear-gradient(135deg, #5c518b 0%, #7b6fb5 100%)',
            }}>
                {organizationInfo.logo ? (
                    <img
                        src={organizationInfo.logo}
                        alt='org logo'
                        style={{ height: 26, width: 26, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid rgba(255,255,255,0.45)', flexShrink: 0 }}
                    />
                ) : (
                    <div style={{ height: 26, width: 26, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className='ri-building-2-line' style={{ color: '#fff', fontSize: '13px' }}></i>
                    </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '12px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
                        {organizationInfo.organization_name || 'Organization'}
                    </div>
                    {todayHours && (
                        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.2, marginTop: '1px' }}>
                            <i className='ri-time-line' style={{ fontSize: '9px', marginRight: '2px' }}></i>
                            Today: {formatTime(todayHours.start)} – {formatTime(todayHours.end)}
                        </div>
                    )}
                </div>
                {/* Toggle button */}
                <button
                    onClick={() => setOpen(o => !o)}
                    title={open ? 'Hide hours' : 'View all hours'}
                    style={{
                        flexShrink: 0,
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.18)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.30)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                >
                    <i
                        className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
                        style={{ color: '#fff', fontSize: '14px', lineHeight: 1 }}
                    ></i>
                </button>
            </div>

            {/* Collapsible hours list */}
            {open && (
                <div style={{ padding: '6px 8px 8px' }}>
                    {hours.map(({ day, short, start, end }) => {
                        const isToday = day === today;
                        return (
                            <div key={day} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '3px 5px',
                                borderRadius: '5px',
                                background: isToday ? 'rgba(92,81,139,0.07)' : 'transparent',
                                marginBottom: '1px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    {isToday
                                        ? <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#5c518b', display: 'inline-block', flexShrink: 0 }}></span>
                                        : <span style={{ width: 5, height: 5, flexShrink: 0 }}></span>
                                    }
                                    <span style={{ fontSize: '11px', fontWeight: isToday ? 700 : 500, color: isToday ? '#5c518b' : '#374151', width: '28px' }}>
                                        {short}
                                    </span>
                                </div>
                                <span style={{ fontSize: '11px', color: isToday ? '#5c518b' : '#6b7280', fontWeight: isToday ? 600 : 400 }}>
                                    {formatTime(start)} – {formatTime(end)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const RightSidebar = ({ onlineUserList }) => {

    let profileData = (useSelector((state) => state.setting.setting.user_data))
    profileData = profileData ? JSON.parse(profileData) : "";

    const organizationInfo = useSelector((state) => state.setting.setting.organization_info);

    const minirightsidebar = () => {
        document.getElementById('rightSidebar').classList.toggle('right-sidebar');
        document.body.classList.toggle('right-sidebar-close');
    }

    return (
        <>
            <div className="right-sidebar-mini" id="rightSidebar">
                <div className="right-sidebar-panel p-0">
                    <Card className="shadow-none">
                        <Card.Body className="p-0">
                            <div className="media-height p-3" data-scrollbar="init">
                                {organizationInfo && <OrgHoursCard organizationInfo={organizationInfo} />}
                                {
                                    (profileData?.user_setting?.is_influencer !== 1 && profileData?.user_setting?.is_influencer !== "1"
                                        && profileData?.user_setting?.is_dance_corner !== 1 && profileData?.user_setting?.is_dance_corner !== "1"
                                        && profileData?.user_type !== 2 && profileData?.user_type !== 4)
                                        ?
                                        <>
                                            <Link key="ai-chat" to={`/chat/index?type=user&slug=${"ai-chat"}`} target='_blank noopener,noreferrer'>
                                                <div className="d-flex align-items-center mb-4">
                                                    <div className="avatar ">
                                                        <img loading="lazy" src={aiProfileImage} alt="chatuserimage" className="avatar-60 rounded-circle img-fluid" />
                                                        <span className="avatar-status"><i className="material-symbols-outlined text-success  md-14 filled">circle</i></span>
                                                    </div>
                                                    <div className="ms-3">
                                                        <h6 className="mb-0">AI Counsellor</h6>
                                                        <p className="mb-0">Chat with AI</p>
                                                    </div>
                                                </div>
                                            </Link>
                                            <Link key="learning-helper" to={`/chat/index?type=user&slug=${"learning-helper"}`} target='_blank noopener,noreferrer'>
                                                <div className="d-flex align-items-center mb-4">
                                                    <div className="avatar ">
                                                        <img loading="lazy" src={learningHelperProfileImage} alt="chatuserimage" className="avatar-60 rounded-circle img-fluid" />
                                                        <span className="avatar-status"><i className="material-symbols-outlined text-success  md-14 filled">circle</i></span>
                                                    </div>
                                                    <div className="ms-3">
                                                        <h6 className="mb-0">Learning Helper</h6>
                                                        <p className="mb-0">Chat with AI</p>
                                                    </div>
                                                </div>
                                            </Link>
                                        </> : null
                                }

                                {
                                    onlineUserList?.map((elm, i) => (
                                        <Link key={elm?.id} to={`/chat/index?type=user&slug=${elm?.slug}`} target='_blank noopener,noreferrer'>
                                            <div className="d-flex align-items-center mb-4">
                                                <div className="avatar ">
                                                    <img loading="lazy" src={elm?.user_setting?.photo ? elm?.user_setting?.photo : "https://uzone360live.s3.amazonaws.com/noimage.png"} alt="chatuserimage" className="avatar-60 rounded-circle img-fluid" />
                                                    {
                                                        elm?.is_online === 1 ?
                                                            <span className="avatar-status"><i className="material-symbols-outlined text-success  md-14 filled">circle</i></span> :
                                                            <span className="avatar-status"><i className="ri-checkbox-blank-circle-fill text-warning"></i></span>
                                                    }
                                                </div>
                                                {/* <div className="iq-profile-avatar status-online">
                                                <Image className="rounded-circle avatar-50"
                                                    src={elm?.user_setting?.photo ? elm?.user_setting?.photo : "https://uzone360live.s3.amazonaws.com/noimage.png"}
                                                    alt="" loading="lazy" />
                                            </div> */}
                                                <div className="ms-3">
                                                    <h6 className="mb-0">{getDisplayName(elm)}</h6>
                                                    <p className="mb-0">{elm?.slug}</p>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}


                                {/* <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-online">
                                        <Image className="rounded-circle avatar-50" src={user1} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Anna Sthesia</h6>
                                        <p className="mb-0">Just Now</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-online">
                                        <Image className="rounded-circle avatar-50" src={user2} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Paul Molive</h6>
                                        <p className="mb-0">Admin</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-online">
                                        <Image className="rounded-circle avatar-50" src={user3} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Anna Mull</h6>
                                        <p className="mb-0">Admin</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-online">
                                        <Image className="rounded-circle avatar-50" src={user4} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Paige Turner</h6>
                                        <p className="mb-0">Admin</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-online">
                                        <Image className="rounded-circle avatar-50" src={user5} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Bob Frapples</h6>
                                        <p className="mb-0">Admin</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-online">
                                        <Image className="rounded-circle avatar-50" src={user2} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Barb Ackue</h6>
                                        <p className="mb-0">Admin</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-online">
                                        <Image className="rounded-circle avatar-50" src={user3} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Greta Life</h6>
                                        <p className="mb-0">Admin</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-away">
                                        <Image className="rounded-circle avatar-50" src={user6} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Ira Membrit</h6>
                                        <p className="mb-0">Admin</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-away">
                                        <Image className="rounded-circle avatar-50" src={user1} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Pete Sariya</h6>
                                        <p className="mb-0">Admin</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center mb-4">
                                    <div className="iq-profile-avatar status-online">
                                        <Image className="rounded-circle avatar-50" src={user2} alt="" loading="lazy" />
                                    </div>
                                    <div className="ms-3">
                                        <h6 className="mb-0">Monty Carlo</h6>
                                        <p className="mb-0">Admin</p>
                                    </div>
                                </div> */}
                            </div>
                            <div className="right-sidebar-toggle bg-primary text-white mt-3 d-flex" onClick={minirightsidebar}>
                                <span className="material-symbols-outlined">chat</span>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

        </>
    )
}

export default RightSidebar
